import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Card, Flex, Text, Avatar } from "@chakra-ui/react";
import { Check } from "~/icons";

interface Physician {
  name: string;
  specialty: string;
  imageUrl?: string;
}

const physicians: Physician[] = [
  {
    name: "Hunter",
    specialty: "Fertility Nurse",
    imageUrl: "https://cdn-s.acuityscheduling.com/calendar-11901476.png?1744644197",
  },
  {
    name: "Whitney",
    specialty: "Fertility Nurse",
    imageUrl: "https://cdn-s.acuityscheduling.com/calendar-10801383.png?1727117322",
  },
  {
    name: "Brianna",
    specialty: "Fertility Nurse",
    imageUrl: "https://cdn-s.acuityscheduling.com/calendar-10814516.png?v=1727119506?1727119507",
  },
];

export const UnderstandingResults: FC = () => {
  const { t } = useTranslation();

  const benefits = [
    t("diagnosticDashboard.understandingResults.benefits.insights"),
    t("diagnosticDashboard.understandingResults.benefits.interpretation"),
    t("diagnosticDashboard.understandingResults.benefits.nextSteps"),
  ];

  return (
    <Box mx={{ base: -4, md: -6 }} width="calc(100% + 32px)" sx={{ "@media (min-width: 768px)": { width: "calc(100% + 48px)" } }}>
      {/* Top semi-circle */}
      <Box
        w="100%"
        h="80px"
        bg="#E8EFFC"
        sx={{
          clipPath: 'ellipse(50% 100% at 50% 100%)',
        }}
      />

      {/* Middle rectangle with content */}
      <Box bg="#E8EFFC" w="100%" py={{ base: 8, md: 12 }} px={{ base: 8, md: 12 }}>
        <Flex direction={{ base: "column", lg: "row" }} gap={8} align="center" maxW="container.xl" mx="auto">
          <Box flex={1}>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight="bold" mb={4}>
              {t("diagnosticDashboard.understandingResults.heading")}
            </Text>
            <Text fontSize="md" color="gray.600" mb={6}>
              {t("diagnosticDashboard.understandingResults.description")}
            </Text>

            <Flex direction="column" gap={3} mb={6}>
              {benefits.map((benefit, index) => (
                <Flex key={index} align="center" gap={2}>
                  <Box
                    flexShrink={0}
                    bg="white"
                    borderRadius="full"
                    width="24px"
                    height="24px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Check color="#111A18" width="14px" height="14px" strokeWidth="2.5" />
                  </Box>
                  <Text fontSize="16px" fontWeight="700" color="#111A18" lineHeight="1.4">
                    {benefit}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Box>

          <Box position="relative" width={{ base: "100%", lg: "400px" }} height={{ base: "auto", lg: "300px" }}>
            <Box
              position="absolute"
              top="-40px"
              right="-40px"
              width="160px"
              height="160px"
              display={{ base: "none", lg: "block" }}
              zIndex={0}
            >
              <svg width="160" height="160" viewBox="0 0 160 160" fill="none">
                <g opacity="0.3">
                  {[...Array(24)].map((_, i) => (
                    <line
                      key={i}
                      x1="80"
                      y1="20"
                      x2="80"
                      y2="50"
                      stroke="#B8CCEF"
                      strokeWidth="2"
                      transform={`rotate(${i * 15} 80 80)`}
                    />
                  ))}
                </g>
              </svg>
            </Box>

            <Box position={{ base: "static", lg: "relative" }} width="100%">
              {physicians.map((physician, index) => (
                <Card
                  key={index}
                  position={{ base: "static", lg: "relative" }}
                  ml={{ base: 0, lg: `${index * 20}px` }}
                  mt={{ base: 0, lg: index > 0 ? 2 : 0 }}
                  bg="white"
                  p={4}
                  borderRadius="16px"
                  boxShadow="md"
                  width={{ base: "100%", lg: "280px" }}
                  mb={{ base: 3, lg: 3 }}
                  _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                  transition="all 0.2s"
                >
                  <Flex align="center" gap={3}>
                    <Avatar
                      size="md"
                      name={physician.name}
                      src={physician.imageUrl}
                    />
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">
                        {physician.name}
                      </Text>
                      <Text fontSize="xs" color="gray.600">
                        {physician.specialty}
                      </Text>
                    </Box>
                  </Flex>
                </Card>
              ))}
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* Bottom semi-circle */}
      <Box
        w="100%"
        h="80px"
        bg="#E8EFFC"
        sx={{
          clipPath: 'ellipse(50% 100% at 50% 0%)',
        }}
      />
    </Box>
  );
};
